import type {
  PlayerPoolEntry,
  SquadPlayer,
  TennisMatch,
  TennisMatchPerformance,
  TennisPerformanceStat,
  TennisScoringConfig,
  TennisSquadRules,
} from '../types/database';
import { calculateTennisMatchPoints, calculateTennisSquadPoints } from './pointsCalculation';

export interface SetScore {
  gamesWon: number;
  gamesLost: number;
}

/**
 * Normalize a player name for matching across data sources. Strips
 * punctuation (periods, commas), collapses whitespace, lower-cases.
 *
 *   "J. De Jong"      → "j de jong"
 *   "Jelle  De Jong." → "jelle de jong"
 *   "Iga Świątek"     → "iga świątek"   (accents preserved on purpose;
 *                                         api-tennis keeps them too)
 */
export function normalizeName(name: string | undefined | null): string {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Build the canonical match key for an api-tennis name. The API always
 * uses "Initial. Lastname" (or "Initial. Initial. Lastname"), so:
 *
 *   "J. Sinner"           → "j sinner"
 *   "A. De Minaur"        → "a de minaur"
 *   "F. Auger Aliassime"  → "f auger aliassime"
 *   "J. P. Ficovich"      → "j ficovich"
 *   "L. van Assche"       → "l van assche"
 *   "G. Mpetshi Perricard"→ "g mpetshi perricard"
 *
 * Everything before the first multi-letter token is treated as initials
 * (we keep only the first one), everything from the first multi-letter
 * token onward is the surname.
 */
export function apiCanonicalKey(name: string | undefined | null): string {
  const cleaned = (name || '')
    .toLowerCase()
    .replace(/[-,.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return '';
  const tokens = cleaned.split(' ').filter(Boolean);
  if (tokens.length === 0) return '';
  if (tokens.length === 1) return tokens[0];
  let surnameStart = 1;
  while (surnameStart < tokens.length && tokens[surnameStart].length === 1) {
    surnameStart++;
  }
  const firstInitial = tokens[0][0];
  const surname = tokens.slice(surnameStart).join(' ');
  if (!surname) return tokens.join(' ');
  return `${firstInitial} ${surname}`;
}

/**
 * Produce multiple candidate match keys for a pool entry name. Pool
 * formats vary in the wild:
 *
 *   "Sinner Jannik"          (Surname Firstname — user's actual format)
 *   "Jannik Sinner"          (Firstname Lastname)
 *   "J. Sinner"              (already in api form)
 *   "De Minaur Alex"         (multi-word Surname, then Firstname)
 *   "Jose Pedro Ficovich"    (Firstname Middlename Lastname)
 *   "Auger-Aliassime Felix"  (hyphenated surname first)
 *
 * Returns every reasonable interpretation so a lookup can succeed
 * regardless of which format the admin used at import time.
 */
export function poolCandidateKeys(name: string | undefined | null): string[] {
  const cleaned = (name || '')
    .toLowerCase()
    .replace(/[-,.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return [];
  const tokens = cleaned.split(' ').filter(Boolean);
  if (tokens.length === 0) return [];
  if (tokens.length === 1) return [tokens[0]];

  const out = new Set<string>();

  // Interpretation A: "Firstname [Middle...] Lastname" — last token is the surname.
  //   "Jannik Sinner"        → "j sinner"
  //   "Jose Pedro Ficovich"  → "j ficovich"
  out.add(`${tokens[0][0]} ${tokens[tokens.length - 1]}`);
  // Variant: Firstname + everything-after-it as multi-word surname.
  //   "Jannik Sinner"        → "j sinner"  (same as above)
  //   "Iga Świątek"          → "i świątek"
  out.add(`${tokens[0][0]} ${tokens.slice(1).join(' ')}`);

  // Interpretation B: "Lastname [Lastname...] Firstname" — first token(s) are the surname,
  // last token is the firstname.
  //   "Sinner Jannik"        → "j sinner"
  //   "De Minaur Alex"       → "a de minaur"
  //   "Auger Aliassime Felix"→ "f auger aliassime"
  const lastIdx = tokens.length - 1;
  out.add(`${tokens[lastIdx][0]} ${tokens.slice(0, lastIdx).join(' ')}`);

  // Already-api-form: "J. Sinner" → canonical handles it.
  const canonical = apiCanonicalKey(name);
  if (canonical) out.add(canonical);

  return Array.from(out);
}

/**
 * Build an index of `apiCanonicalKey(name) → playerKey` from any source
 * of API players (the `/tennis/data/players` collection, or names
 * scraped out of matches). Last write wins on collision (rare in practice).
 */
export function buildApiKeyByName(
  entries: Array<{ playerKey: number | undefined | null; name: string | undefined | null }>
): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of entries) {
    if (typeof e.playerKey !== 'number' || !e.name) continue;
    const key = apiCanonicalKey(e.name);
    if (key) out.set(key, e.playerKey);
  }
  return out;
}

/**
 * Convenience wrapper to build the index directly from a list of synced
 * tennisMatches docs (used by `fetchImportCandidates`).
 */
export function buildApiKeyByNameFromMatches(matches: TennisMatch[]): Map<string, number> {
  const entries: Array<{ playerKey: number; name: string }> = [];
  for (const m of matches) {
    if (typeof m.firstPlayerKey === 'number' && m.firstPlayerName) {
      entries.push({ playerKey: m.firstPlayerKey, name: m.firstPlayerName });
    }
    if (typeof m.secondPlayerKey === 'number' && m.secondPlayerName) {
      entries.push({ playerKey: m.secondPlayerKey, name: m.secondPlayerName });
    }
  }
  return buildApiKeyByName(entries);
}

/**
 * Build an opponent-seed lookup that survives the pool ↔ api name-format
 * mismatch. Pool entries store names like "Alcaraz Carlos" / "Carlos Alcaraz"
 * / "J. Sinner"; tennis matches always reference opponents in api short form
 * ("C. Alcaraz"). A naive normalize-and-compare never hits — so we index
 * every seeded pool entry under all of its `poolCandidateKeys` and look it
 * up using `apiCanonicalKey(opponentName)`.
 *
 * The returned function is the contract passed to
 * `buildPerformanceFromTennisMatch` as `opponentSeedLookup`.
 */
export function buildOpponentSeedLookup(
  players: { name: string; seed?: number }[]
): (opponentName: string) => number | undefined {
  const seedByKey = new Map<string, number>();
  for (const p of players) {
    if (p.seed === undefined || p.seed === null) continue;
    for (const k of poolCandidateKeys(p.name)) {
      seedByKey.set(k, p.seed);
    }
  }
  return (opponentName: string) => {
    const canonical = apiCanonicalKey(opponentName);
    if (!canonical) return undefined;
    return seedByKey.get(canonical);
  };
}

/**
 * Self-healing lookup from a squad-stored player reference to the current
 * pool entry. Used by the recompute paths so a squad whose `playerId` no
 * longer exists in the pool (the Zuber/Matteo failure mode) doesn't silently
 * freeze — it falls back to canonical-name match.
 *
 *   matchedBy = 'playerId'      → exact id hit; safe to use
 *   matchedBy = 'canonicalName' → id missing, name unique → safe to auto-patch
 *   matchedBy = 'none'          → no match, or name is ambiguous (≥2 entries
 *                                 with the same canonical key); caller must
 *                                 surface a warning rather than guess
 */
export function resolveSquadPlayerInPool<
  P extends { playerId: string; name: string }
>(
  squadPlayer: { playerId: string; playerName: string },
  poolPlayers: P[],
): { poolEntry: P | null; matchedBy: 'playerId' | 'canonicalName' | 'none' } {
  // 1. Exact id match always wins
  if (squadPlayer.playerId) {
    const byId = poolPlayers.find((p) => p.playerId === squadPlayer.playerId);
    if (byId) return { poolEntry: byId, matchedBy: 'playerId' };
  }

  // 2. Canonical name fallback — only safe when the name resolves to exactly
  // one pool entry. Ambiguity (e.g. Cerundolo brothers) returns 'none' so the
  // caller surfaces a warning instead of mis-crediting points.
  const wantedKey = apiCanonicalKey(squadPlayer.playerName);
  if (!wantedKey) return { poolEntry: null, matchedBy: 'none' };

  const candidates = poolPlayers.filter((p) => {
    if (apiCanonicalKey(p.name) === wantedKey) return true;
    return poolCandidateKeys(p.name).includes(wantedKey);
  });
  if (candidates.length === 1) {
    return { poolEntry: candidates[0], matchedBy: 'canonicalName' };
  }

  return { poolEntry: null, matchedBy: 'none' };
}

/**
 * Pure recompute of a single tennis squad's points against the latest pool.
 *
 * Three lookup tiers per squad player:
 *   1. Exact playerId match in `playerUpdates` (legacy sparse-update path).
 *   2. If `poolPlayers` provided and step 1 missed, resolveSquadPlayerInPool
 *      tries id-then-canonical-name against the full pool. A canonical-name
 *      hit is recorded as an `idPatch` so the caller can self-heal the
 *      squad's stored playerId.
 *   3. Anything left unresolved produces a human-readable entry in `warnings`
 *      so the admin sees a real signal instead of points silently freezing.
 *
 * Old callers that pass no `poolPlayers` get the original behavior verbatim:
 * unmatched squad players are passed through unchanged with no warnings.
 */
export function recomputeSquadFromPool(params: {
  squad: {
    players: SquadPlayer[];
    mensPrediction?: string;
    womensPrediction?: string;
    bankedPoints?: number;
  };
  playerUpdates: { playerId: string; points: number }[];
  poolPlayers?: { playerId: string; name: string; points: number }[];
  rules: TennisSquadRules;
  config: TennisScoringConfig;
  mensTournamentWinnerId?: string;
  womensTournamentWinnerId?: string;
}): {
  updatedPlayers: SquadPlayer[];
  totalPoints: number;
  predictionBonusPoints: number;
  warnings: string[];
  idPatches: { from: string; to: string; playerName: string }[];
} {
  const { squad, playerUpdates, poolPlayers, rules, config } = params;
  const warnings: string[] = [];
  const idPatches: { from: string; to: string; playerName: string }[] = [];

  const updatedPlayers: SquadPlayer[] = squad.players.map((p) => {
    const direct = playerUpdates.find((u) => u.playerId === p.playerId);
    if (direct) return { ...p, points: direct.points };

    if (!poolPlayers) return p; // legacy callers — no self-healing

    const resolved = resolveSquadPlayerInPool(
      { playerId: p.playerId, playerName: p.playerName },
      poolPlayers,
    );
    if (resolved.matchedBy === 'playerId' && resolved.poolEntry) {
      return { ...p, points: resolved.poolEntry.points };
    }
    if (resolved.matchedBy === 'canonicalName' && resolved.poolEntry) {
      idPatches.push({
        from: p.playerId,
        to: resolved.poolEntry.playerId,
        playerName: p.playerName,
      });
      return { ...p, playerId: resolved.poolEntry.playerId, points: resolved.poolEntry.points };
    }
    warnings.push(
      `Squad player "${p.playerName}" (id ${p.playerId}) not found in current pool by id or canonical name`,
    );
    return p;
  });

  const { totalPoints, predictionBonusPoints } = calculateTennisSquadPoints(
    updatedPlayers,
    rules,
    squad.mensPrediction,
    squad.womensPrediction,
    params.mensTournamentWinnerId,
    params.womensTournamentWinnerId,
    config,
    squad.bankedPoints ?? 0,
  );

  return { updatedPlayers, totalPoints, predictionBonusPoints, warnings, idPatches };
}

/**
 * Resolve an api-tennis playerKey for a pool entry name by trying each
 * candidate key in turn. Returns undefined if no candidate hits.
 */
export function lookupApiKey(
  index: Map<string, number>,
  name: string | undefined | null
): number | undefined {
  for (const key of poolCandidateKeys(name)) {
    const hit = index.get(key);
    if (typeof hit === 'number') return hit;
  }
  return undefined;
}

/**
 * Derive the straight-sets and final-set tie-break flags from a list of set
 * scores. Mirrors the auto-derivation in TennisMatchResultDialog so manual
 * entry, API import, and the admin dialog all agree.
 */
export function deriveSetFlags(
  setScores: SetScore[],
  won: boolean,
  opponentWithdrew = false,
  gender?: 'men' | 'women'
): { straightSets: boolean; finalSetTieBreak: boolean } {
  if (opponentWithdrew) {
    // Walkover — counted as a straight-set win, no sets actually played.
    return { straightSets: true, finalSetTieBreak: false };
  }

  // Count only sets that actually closed. Retirement scorelines like "3-1"
  // are partial and must not contribute to the straight-sets tally.
  const closedSets = setScores.filter(isClosedSet);
  const wonSets = closedSets.filter(s => s.gamesWon > s.gamesLost).length;
  const lostSets = closedSets.filter(s => s.gamesWon < s.gamesLost).length;
  // A straight-sets sweep requires the winner to have closed every set they
  // played, AND to have hit the minimum sweep count for the format (3 for
  // best-of-5 men's, 2 for best-of-3 women's). Unknown gender → ≥ 2.
  const sweepRequired = gender === 'men' ? 3 : 2;
  const straightSets = won && lostSets === 0 && wonSets >= sweepRequired;

  const last = setScores[setScores.length - 1];
  const lastWasTieBreak =
    !!last &&
    ((last.gamesWon === 7 && last.gamesLost === 6) ||
      (last.gamesWon === 6 && last.gamesLost === 7));

  // The final-set tie-break bonus is only awarded when the tie-break occurred
  // in the actual DECIDING set:
  //   - Men's grand slam (best of 5)  → set 5
  //   - Women's grand slam (best of 3) → set 3
  // A tie-break in any earlier set (e.g., set 4 of a men's match that
  // ended 3-1) does NOT count. When gender is unspecified we fall back to
  // the looser rule so legacy callers don't silently lose the bonus.
  let finalSetTieBreak: boolean;
  if (gender === 'men') {
    finalSetTieBreak = lastWasTieBreak && setScores.length === 5;
  } else if (gender === 'women') {
    finalSetTieBreak = lastWasTieBreak && setScores.length === 3;
  } else {
    finalSetTieBreak = lastWasTieBreak;
  }

  return { straightSets, finalSetTieBreak };
}

/**
 * Parse an api-tennis set score pair into integer game counts. The API encodes
 * tie-break sets with dot notation ("7.7" / "6.5" means 7 games vs 6 games);
 * we keep only the games count (the integer part before the dot).
 */
export function parseSetGames(
  scoreFirst: string,
  scoreSecond: string
): { first: number; second: number } {
  const toGames = (v: string): number => {
    if (typeof v !== 'string' || v.trim() === '') return 0;
    const intPart = v.split('.')[0];
    const n = parseInt(intPart, 10);
    return Number.isNaN(n) ? 0 : n;
  };
  return { first: toGames(scoreFirst), second: toGames(scoreSecond) };
}

function readStat(
  match: TennisMatch,
  side: 'first' | 'second',
  names: string[]
): number | undefined {
  const lowered = names.map(n => n.toLowerCase());
  const stat = match.statistics?.find(
    s => s.period === 'match' && lowered.includes((s.statName || '').toLowerCase())
  );
  if (!stat) return undefined;
  const value = side === 'first' ? stat.firstPlayerValue : stat.secondPlayerValue;
  return value === null || value === undefined ? undefined : value;
}

/**
 * Collect every stat row from a synced match, scoped to one side of the
 * match. Includes all periods (match + set1/set2/...) and skips rows where
 * the API returned no value for the requested side.
 */
export function extractAllStatsForSide(
  match: TennisMatch,
  side: 'first' | 'second'
): TennisPerformanceStat[] {
  const out: TennisPerformanceStat[] = [];
  for (const s of match.statistics || []) {
    const value = side === 'first' ? s.firstPlayerValue : s.secondPlayerValue;
    const raw = side === 'first' ? s.firstPlayerRaw : s.secondPlayerRaw;
    const won = side === 'first' ? s.firstPlayerWon : s.secondPlayerWon;
    const total = side === 'first' ? s.firstPlayerTotal : s.secondPlayerTotal;
    // Skip rows where the API returned absolutely nothing for this side.
    if (value === null && (raw === null || raw === '')) continue;
    const row: TennisPerformanceStat = {
      statName: s.statName,
      statType: s.statType,
      period: s.period,
      value: value ?? null,
      raw: raw ?? null,
    };
    if (won != null) row.won = won;
    if (total != null) row.total = total;
    out.push(row);
  }
  return out;
}

/**
 * Build a per-match performance record for the given player from a synced
 * tennisMatches document. Returns null when the match is not finished, has no
 * winner, or the player did not play in it.
 *
 * @param opponentSeedLookup optional resolver mapping an opponent name to a
 *   tournament seed (used to award upset bonuses on import).
 */
/**
 * A set is only "won" if the leading side has the games count needed to close
 * the set: ≥ 6 games with a ≥ 2-game margin, or 7 games (covers 7-5 and the
 * 7-6 tie-break). Anything below that (e.g. 3-1 after a retirement) is an
 * incomplete set and must not be counted toward set tallies.
 */
export function isClosedSet(s: SetScore): boolean {
  const lead = Math.max(s.gamesWon, s.gamesLost);
  const trail = Math.min(s.gamesWon, s.gamesLost);
  if (!Number.isFinite(lead) || !Number.isFinite(trail)) return false;
  return (lead >= 6 && lead - trail >= 2) || lead === 7;
}

/**
 * Hard check: a match is eligible for import when no more sets are expected.
 * That covers normal completions AND retirements (the loser quit, the winner
 * is declared final). Filters out matches still in progress, suspended, or
 * walkovers with no declared winner.
 *
 *   ALLOW  status === 'Finished' AND winner ∈ {'First Player','Second Player'}
 *   DENY   Live / Suspended / scheduled / no real winner
 *
 * For retirements we tolerate partial scorelines — the bonus tallies in
 * `deriveSetFlags` rely on `isClosedSet` so a partial last set does not
 * inflate the straight-sets / dominant-set check.
 */
export function isMatchTrulyCompleted(match: TennisMatch): boolean {
  if (match.status !== 'Finished') return false;
  if (match.winner !== 'First Player' && match.winner !== 'Second Player') return false;
  // Require at least one scored set so we don't import phantom "Finished"
  // matches with no scoreboard data. Real retirements always have at least
  // one closed set on the board.
  const sets = match.scores ?? [];
  return sets.length > 0;
}

export function buildPerformanceFromTennisMatch(
  match: TennisMatch,
  playerKey: number,
  playerSeed: number | undefined,
  config: TennisScoringConfig,
  opponentSeedLookup?: (name: string) => number | undefined,
  playerGender?: 'men' | 'women'
): TennisMatchPerformance | null {
  if (!isMatchTrulyCompleted(match)) return null;

  let side: 'first' | 'second';
  if (match.firstPlayerKey === playerKey) side = 'first';
  else if (match.secondPlayerKey === playerKey) side = 'second';
  else return null;

  const won =
    (side === 'first' && match.winner === 'First Player') ||
    (side === 'second' && match.winner === 'Second Player');

  const opponentName = side === 'first' ? match.secondPlayerName : match.firstPlayerName;

  const setScores: SetScore[] = (match.scores ?? []).map(s => {
    const { first, second } = parseSetGames(s.scoreFirst, s.scoreSecond);
    return side === 'first'
      ? { gamesWon: first, gamesLost: second }
      : { gamesWon: second, gamesLost: first };
  });

  const { straightSets, finalSetTieBreak } = deriveSetFlags(setScores, won, false, playerGender);

  const opponentSeed = opponentSeedLookup ? opponentSeedLookup(opponentName) : undefined;

  const pointsEarned = calculateTennisMatchPoints(
    won,
    straightSets,
    finalSetTieBreak,
    setScores.map(s => s.gamesWon),
    setScores.map(s => s.gamesLost),
    playerSeed,
    opponentSeed,
    config
  );

  return {
    id: `${match.eventKey}-${playerKey}`,
    matchId: String(match.eventKey),
    round: match.round ?? undefined,
    opponentName,
    opponentSeed,
    won,
    setScores,
    straightSets,
    finalSetTieBreak,
    aces: readStat(match, side, ['Aces']),
    doubleFaults: readStat(match, side, ['Double Faults', 'Double faults']),
    firstServePct: readStat(match, side, ['1st Serve', '1st serve percentage', 'First Serve']),
    winners: readStat(match, side, ['Winners']),
    unforcedErrors: readStat(match, side, ['Unforced Errors']),
    breakPointsWon: readStat(match, side, ['Break Points Won', 'Break Points Converted']),
    breakPointsTotal: readStat(match, side, ['Break Points', 'Break Points Total']),
    // Capture every stat the API returned for this side — all periods, all
    // categories — so nothing is lost on import.
    allStats: extractAllStatsForSide(match, side),
    pointsEarned,
    source: 'import',
    date: match.dateTimeUTC ? new Date(match.dateTimeUTC) : new Date(),
  };
}

/** Total points for a pool entry = sum of every match record's pointsEarned. */
export function recomputeEntryPoints(entry: PlayerPoolEntry): number {
  const records = entry.tennisMatchPerformances ?? [];
  return records.reduce((sum, p) => sum + (p.pointsEarned || 0), 0);
}

/**
 * Drop candidate performances whose matchId already appears in the existing
 * list. Candidates without a matchId (manual entries) are always kept.
 */
export function dedupeNewPerformances(
  existing: TennisMatchPerformance[],
  candidates: TennisMatchPerformance[]
): TennisMatchPerformance[] {
  const seen = new Set(
    (existing ?? []).map(p => p.matchId).filter((id): id is string => !!id)
  );
  return candidates.filter(c => !c.matchId || !seen.has(c.matchId));
}

/**
 * Keep only candidate performances whose `date` falls on or before the end of
 * `cutoffYMD` in local time. Used by the admin "Import from API" dialog to
 * limit a run to matches up through a chosen day. Empty/undefined cutoff is
 * treated as "no filter" and the input is returned unchanged. Candidates left
 * with no performances after filtering are dropped.
 */
export function filterImportCandidatesByCutoff<
  T extends { performances: TennisMatchPerformance[] }
>(candidates: T[], cutoffYMD: string | undefined): T[] {
  if (!cutoffYMD) return candidates;
  const cutoff = new Date(`${cutoffYMD}T23:59:59.999`).getTime();
  if (Number.isNaN(cutoff)) return candidates;

  const result: T[] = [];
  for (const c of candidates) {
    const kept = c.performances.filter(p => {
      const t = p.date instanceof Date ? p.date.getTime() : new Date(p.date as any).getTime();
      return !Number.isNaN(t) && t <= cutoff;
    });
    if (kept.length > 0) {
      result.push({ ...c, performances: kept });
    }
  }
  return result;
}
